import { Box, FormControlLabel, Stack, Switch, TextField } from '@mui/material';
import * as refine from '@refinedev/core';
import { Create, Edit, useAutocomplete } from '@refinedev/mui';
import { axiosInstance } from '@refinedev/simple-rest';
import dayjs from 'dayjs';
import { useEffect, useRef, useState } from 'react';
import { useForm, FormProvider, Controller } from 'react-hook-form';
import { useSearchParams } from 'react-router';
import {
  DOCUMENT_TYPES,
  DOCUMENT_TYPE_KEYS,
  PREMIUM_MAGAZINE_OPTIONS,
  PREMIUM_MAGAZINE_OPTION_KEYS,
  REGIONS,
  REGION_KEYS,
} from 'shiver-core/static';
import { uuidv7 } from 'uuidv7';
import { BaseField } from '../../../components/fields/BaseField.tsx';
import { DateTimeField } from '../../../components/fields/DateTimeField.tsx';
import { ImageField, type ImageInfo } from '../../../components/fields/ImageField.tsx';
import { ListField } from '../../../components/fields/ListField.tsx';
import { MagazineArticleListField } from '../../../components/fields/MagazineArticleListField.tsx';
import { RichTextField3 } from '../../../components/fields/RichTextField3.tsx';
import { SelectField } from '../../../components/fields/SelectField.tsx';
import { SortableListField } from '../../../components/fields/SortableListField.tsx';
import { getAuthorizedFile } from '../../../components/lib/file.ts';
import { Image } from '../../../components/lib/image.ts';
import type { DocumentHead, Sector, Series, TimelineHead, Topic } from '../../../types/index.ts';
import { Author } from '../../../types/index.ts';
import {
  DefaultDocument,
  Document,
  DocumentId,
  MagazineArticle,
  MagazineToc,
} from '../../../types/index.ts';
import { AttachmentListField } from './AttachmentListField.tsx';
import { ExternalDatabaseUrlField } from './ExternalDatabaseUrlField.tsx';
import { PDFField } from './PDFField.tsx';

const COMPONENT_MAP = { create: Create, edit: Edit } as const;

export const DocumentForm = ({ action }: { action: 'create' | 'edit' }) => {
  const [searchParams] = useSearchParams();

  const [newId] = useState(() => DocumentId.parse(uuidv7()));

  // 複製機能
  // ?clone=<id> パラメータで新規作成する場合、元ドキュメントを取得してデータをフォームに事前入力する
  const cloneId = action === 'create' ? searchParams.get('clone') : null;

  const { query, formLoading, onFinish } = refine.useForm<Document>({
    resource: 'contents/documents',
    action,
    id: cloneId ?? undefined,
    queryOptions: {
      enabled: action === 'edit' || !!cloneId,
      select({ data }) {
        const parsed = Document.parse(data);
        return {
          data: cloneId
            ? {
                ...parsed,
                // 新しいIDを生成
                id: newId,
                // タイトルに「（コピー）」を追加
                title: `${data.title}（コピー）`,
                // 公開日時をクリア（下書きとして扱う）
                publishedAt: null,
                updatedAt: null,
                // 画像をクリア（競合を避けるため）
                featuredImageKey: null,
                thumbnailImageKey: null,
                // 雑誌目次との紐づけと雑誌目次上の並び順をリセット
                magazineTocId: null,
                order: 0,
              }
            : parsed,
        };
      },
    },
  });

  const data = query?.data?.data;

  // 雑誌記事のフォームロード時の初期値を保持するための変数を宣言（雑誌目次紐づき済みシリーズの変更判定・モーダル表示条件に使用）
  const initialDocumentSeriesIdsRef = useRef<string[]>([]);
  const initialMagazineTocIdRef = useRef<string | null | undefined>(undefined);
  const initialTocSeriesIdsRef = useRef<string[]>([]);

  const methods = useForm<Document>({
    values: data,
    defaultValues: {
      id: newId,
      featuredImageKey: null,
      thumbnailImageKey: null,
      lede: null,
      textHtml: null,
      authors: [],
      regions: ['japan' as const],
      publishedAt: null,
      updatedAt: null,
      sectors: [],
      topics: [],
      series: [],
      timelines: [],
      isFeatured: false,
      relatedDocuments: [],
      tkcLawLibraryUrl: null,
      westlawJapanUrl: null,
      shojiHomuDbUrl: null,
      junkanShojiHomuDbUrl: null,
    },
  });

  const { setWarnWhen } = refine.useWarnAboutChange();
  useEffect(() => {
    setWarnWhen(methods.formState.isDirty);
  }, [methods.formState.isDirty, setWarnWhen]);
  const { autocompleteProps: authorAutocompleteProps } = useAutocomplete<Author>({
    resource: 'contents/authors',
    sort: [{ field: 'name', order: 'asc' }],
    onSearch: (value) => [{ field: 'name+jobTitle', operator: 'contains', value }],
    queryOptions: {
      select({ data, total }) {
        return {
          data: data.map((item) => Author.parse(item)),
          total,
        };
      },
    },
  });
  const { autocompleteProps: sectorAutocompleteProps } = useAutocomplete<Sector>({
    resource: 'contents/sectors',
    sort: [
      { field: 'category', order: 'asc' },
      { field: 'name', order: 'asc' },
    ],
    onSearch: (value) => [{ field: 'category+name', operator: 'contains', value }],
  });
  const { autocompleteProps: topicAutocompleteProps } = useAutocomplete<Topic>({
    resource: 'contents/topics',
    sort: [
      { field: 'category', order: 'asc' },
      { field: 'name', order: 'asc' },
    ],
    onSearch: (value) => [{ field: 'category+name', operator: 'contains', value }],
  });
  const { autocompleteProps: seriesAutocompleteProps } = useAutocomplete<Series>({
    resource: 'contents/series',
    sort: [{ field: 'name', order: 'asc' }],
    onSearch: (value) => [{ field: 'name', operator: 'contains', value }],
  });
  const { autocompleteProps: timelineAutocompleteProps } = useAutocomplete<TimelineHead>({
    resource: 'contents/timelines',
    sort: [{ field: 'name', order: 'asc' }],
    onSearch: (value) => [{ field: 'name', operator: 'contains', value }],
  });
  const { autocompleteProps: relatedDocumentAutocompleteProps } = useAutocomplete<DocumentHead>({
    resource: 'contents/documents',
    sort: [{ field: 'title', order: 'asc' }],
    onSearch: (value) => [{ field: 'title', operator: 'contains', value }],
  });

  const Component = COMPONENT_MAP[action];

  const [imageInfo, setImageInfo] = useState<ImageInfo | null>(null);
  const [shouldUseAsFeaturedImage, setShouldUseAsFeaturedImage] = useState<boolean>(true);

  if (data?.thumbnailImageKey && !data?.featuredImageKey && shouldUseAsFeaturedImage) {
    setShouldUseAsFeaturedImage(false);
  }

  const [pdfFile, setPdfFile] = useState<File | null>(null);

  useEffect(() => {
    if (data?.type === 'magazine_article' && !pdfFile) {
      getAuthorizedFile(`/.api/v1/contents/media/${data.pdfFileKey}`).then(setPdfFile);
    }
  }, [data, pdfFile]);

  // 雑誌記事のシリーズ変更による雑誌目次紐づけ解除判定のため、フォームを開いた時点の初期値を格納
  // DBからのデータ取得完了後初回のみセット
  useEffect(() => {
    if (
      data?.type === 'magazine_article' &&
      !cloneId &&
      !query?.isFetching &&
      initialMagazineTocIdRef.current === undefined
    ) {
      initialDocumentSeriesIdsRef.current = data.series.map((s) => s.id);
      initialMagazineTocIdRef.current = data.magazineTocId;
      initialTocSeriesIdsRef.current = data.tocSeriesIds ?? [];
    }
  }, [data, cloneId, query?.isFetching]);

  const documentType = methods.watch('type');

  return (
    <Component
      canDelete
      isLoading={formLoading}
      saveButtonProps={{
        disabled: methods.formState.isSubmitting || formLoading || query?.isFetching,
        onClick: methods.handleSubmit(async (data) => {
          if (!data.id) {
            throw new Error('Document ID is missing');
          }

          const now = new Date();
          // e.g. now=15:30, publishedAt=15:29 を許容する
          now.setMinutes(0);
          now.setSeconds(0);
          now.setMilliseconds(0);

          // 複製操作時は作成日時・変更日時を複製した日時に設定
          if (cloneId) {
            data.createdAt = new Date();
            data.modifiedAt = new Date();
          }

          if (
            methods.getFieldState('publishedAt').isDirty &&
            data.publishedAt &&
            data.publishedAt < now &&
            !window.confirm(
              '公開日時が過去に設定されているため、新着通知メールが送信されない可能性があります。',
            )
          ) {
            methods.setError('publishedAt', {
              type: 'manual',
              message: '公開日時を修正してください',
            });
            return;
          }

          if (
            methods.getFieldState('updatedAt').isDirty &&
            data.updatedAt &&
            data.updatedAt < now &&
            !window.confirm(
              '更新日時が過去に設定されているため、新着通知メールが送信されない可能性があります。',
            )
          ) {
            methods.setError('updatedAt', {
              type: 'manual',
              message: '更新日時を修正してください',
            });
            return;
          }

          if (data.type === 'magazine_article' && initialMagazineTocIdRef.current != null) {
            const oldDocumentSeriesIds = new Set(initialDocumentSeriesIdsRef.current);
            const newDocumentSeriesIds = new Set(data.series.map((s) => s.id));
            const oldMagazineTocDocumentSeriesIds = new Set(initialTocSeriesIdsRef.current);
            const seriesChanged = [...oldDocumentSeriesIds].some(
              (id) => !newDocumentSeriesIds.has(id) && oldMagazineTocDocumentSeriesIds.has(id),
            );
            if (seriesChanged) {
              if (
                !window.confirm(
                  'シリーズの設定を変更すると、関連する雑誌目次からこの記事が除外されます。よろしいですか？',
                )
              ) {
                return;
              }
              data.magazineTocId = null;
            }
          }

          if (imageInfo) {
            const contentType = 'image/webp';

            let image = await Image.fromDataURL(imageInfo.dataURL);

            if (imageInfo.crop) {
              image = image.crop(imageInfo.crop);
            }

            const thumbnailImage = await image.toBlob({ type: contentType, quality: 1 });
            const thumbnailImageKey = `images/documents/${data.id}/thumbnail.webp`;

            await fetch(`/.api/v1/contents/media/${thumbnailImageKey}`, {
              method: 'POST',
              headers: {
                'content-type': contentType,
                authorization: axiosInstance.defaults.headers.common.Authorization as string,
              },
              body: thumbnailImage,
            });

            data.thumbnailImageKey = thumbnailImageKey;
            data.featuredImageKey = shouldUseAsFeaturedImage ? thumbnailImageKey : null;
          } else {
            data.thumbnailImageKey = null;
            data.featuredImageKey = null;
          }

          let parsed: Document;

          if (data.type === 'magazine_article') {
            if (!pdfFile) {
              throw new Error('PDFファイルを選択してください');
            }

            data.documentId = data.id;
            data.magazineTocId ??= null;
            data.pdfFileKey = `pdfs/magazines/${data.id}.pdf`;

            await fetch(`/.api/v1/contents/media/${data.pdfFileKey}`, {
              method: 'POST',
              headers: {
                'content-type': 'application/pdf',
                'content-disposition': `inline; filename="${data.id}.pdf"`,
                authorization: axiosInstance.defaults.headers.common.Authorization as string,
              },
              body: pdfFile,
            });

            if (data.tkcLawLibraryUrl?.trim() === '') data.tkcLawLibraryUrl = null;
            if (data.westlawJapanUrl?.trim() === '') data.westlawJapanUrl = null;
            if (data.shojiHomuDbUrl?.trim() === '') data.shojiHomuDbUrl = null;
            if (data.junkanShojiHomuDbUrl?.trim() === '') data.junkanShojiHomuDbUrl = null;
            // tocSeriesIds はフロントエンドのシリーズ変更判定専用の導出フィールドのため、APIへの送信ペイロードから除外する
            parsed = MagazineArticle.omit({ tocSeriesIds: true }).parse(data) as Document;
          } else if (data.type === 'magazine_toc') {
            //

            parsed = MagazineToc.parse(data);
          } else {
            if (data.lede?.trim() === '') {
              data.lede = null;
            }

            if (data.textHtml?.trim() === '') {
              data.textHtml = null;
            }

            parsed = DefaultDocument.parse(data);
          }

          return onFinish(parsed);
        }),
      }}
    >
      <FormProvider {...methods}>
        <Stack spacing={2}>
          <Box display="flex" gap={2}>
            <Controller
              name="publishedAt"
              control={methods.control}
              render={({ field, fieldState }) => (
                <DateTimeField
                  {...field}
                  type="datetime-local"
                  label="公開日時"
                  fullWidth
                  sx={{ flex: 1 }}
                  error={!!fieldState.error}
                  helperText={fieldState.error?.message}
                />
              )}
            />
            <SelectField
              name="type"
              label="記事タイプ"
              rules={{ required: '必須' }}
              options={[...DOCUMENT_TYPE_KEYS].sort(
                (a, b) => DOCUMENT_TYPES[a].order - DOCUMENT_TYPES[b].order,
              )}
              getOptionLabel={(option) => DOCUMENT_TYPES[option].name}
              sx={{ flex: 1 }}
            />
            <ListField
              name="regions"
              label={'地域'}
              options={REGION_KEYS}
              getOptionKey={(item) => item}
              getOptionLabel={(option) => REGIONS[option as keyof typeof REGIONS].name}
              sx={{ flex: 2 }}
            />
          </Box>
          <BaseField
            name="title"
            rules={{
              required: '必須',
            }}
            label="タイトル"
            fullWidth
          />
          <BaseField name="lede" label="リード文" fullWidth />
          {documentType === 'magazine_article' ? (
            <>
              <SelectField
                name="magazineKey"
                label="雑誌"
                rules={{ required: '必須' }}
                options={PREMIUM_MAGAZINE_OPTION_KEYS}
                getOptionLabel={(option) => PREMIUM_MAGAZINE_OPTIONS[option].name}
                sx={{ flex: 1, mr: 2 }}
              />
              <PDFField
                label="PDF"
                value={pdfFile}
                onChange={setPdfFile}
                error={!pdfFile}
                helperText="PDFファイルを選択してください"
              />
            </>
          ) : documentType === 'magazine_toc' ? (
            <MagazineArticleListField name="magazineArticles" label="目次" />
          ) : (
            <RichTextField3
              name="textHtml"
              rules={{
                //
              }}
              label="本文"
              fullWidth
              height={500}
              onImageUpload={async (file) => {
                const { id: documentId } = methods.getValues();
                const assetId = uuidv7();
                const key = `images/documents/${documentId}/figures/${assetId}`;
                const contentType = 'image/png';

                // NOTE: shiver-appの配信URL
                const src = `/media/${key}`;

                await fetch(src, {
                  method: 'POST',
                  headers: {
                    'content-type': contentType,
                  },
                  body: await file.arrayBuffer(),
                });

                return src;
              }}
            />
          )}
          <SortableListField
            {...authorAutocompleteProps}
            name="authors"
            label="執筆者"
            getOptionKey={(item) => item.id}
            getOptionLabel={(item) => {
              const jobTitlePart = item.jobTitle ? `（${item.jobTitle}）` : '';
              const createdAtPart = item.createdAt
                ? ` (${item.createdAt.toLocaleDateString('ja-JP')})`
                : '';
              return `${item.name}${jobTitlePart}${createdAtPart}`;
            }}
          />
          <ImageField
            label="サムネイル画像"
            aspect={16 / 9}
            imageURL={
              data?.thumbnailImageKey ? `/.api/v1/contents/media/${data?.thumbnailImageKey}` : null
            }
            onChange={setImageInfo}
          />
          <FormControlLabel
            control={
              <Switch
                checked={shouldUseAsFeaturedImage}
                onChange={(e) => {
                  setShouldUseAsFeaturedImage(e.target.checked);
                }}
              />
            }
            label="アイキャッチ画像に設定する"
          />
          {documentType === 'magazine_article' && <ExternalDatabaseUrlField />}
          <AttachmentListField />
          <ListField
            {...sectorAutocompleteProps}
            name="sectors"
            label="分野"
            getOptionKey={(item) => item.id}
            getOptionLabel={(item) => `${item.category.name}／${item.name}`}
          />
          <ListField
            {...topicAutocompleteProps}
            name="topics"
            label="トピック"
            getOptionKey={(item) => item.id}
            getOptionLabel={(item) => `${item.category.name}／${item.name}`}
          />
          <ListField
            {...seriesAutocompleteProps}
            name="series"
            label="シリーズ"
            getOptionKey={(item) => item.id}
            getOptionLabel={(item) => item.name}
          />
          <ListField
            {...timelineAutocompleteProps}
            name="timelines"
            label="タイムライン"
            getOptionKey={(item) => item.id}
            getOptionLabel={(item) => item.name}
          />
          <Box display="flex" gap={2}>
            <SelectField
              name="isFeatured"
              label="注目フラグ（タイムライン）"
              options={[true, false]}
              getOptionLabel={(option) => (option ? 'ON' : 'OFF')}
              sx={{ flex: 1 }}
            />
            {action === 'edit' && (
              <Controller
                name="updatedAt"
                control={methods.control}
                rules={{
                  validate(value) {
                    if (value) {
                      const publishedAt = methods.getValues('publishedAt');

                      if (!publishedAt) {
                        return '公開日時が未設定の記事は更新日時を指定できません';
                      }
                      if (value <= publishedAt) {
                        return '公開日時より前の日時を指定できません';
                      }
                      if (value > new Date()) {
                        return '未来の日時は指定できません';
                      }
                    }
                    return true;
                  },
                }}
                render={({ field, fieldState }) => (
                  <DateTimeField
                    {...field}
                    type="datetime-local"
                    label="更新日時"
                    fullWidth
                    sx={{ flex: 1 }}
                    error={!!fieldState.error}
                    helperText={
                      fieldState.error?.message ?? 'この日時から一週間、更新ラベルが表示されます'
                    }
                  />
                )}
              />
            )}
            <SelectField
              name="isSpecialOpen"
              label="閲覧制限"
              options={[true, false]}
              getOptionLabel={(option) => (option ? '特別一般公開' : '会員向け')}
              sx={{ flex: 1 }}
            />
          </Box>
          <ListField
            {...relatedDocumentAutocompleteProps}
            name="relatedDocuments"
            label="関連記事"
            getOptionKey={(item) => item.id}
            getOptionLabel={(item) =>
              `（${DOCUMENT_TYPES[item.type].name}）${item.title}${item.publishedAt ? dayjs(item.publishedAt).format('（YYYY/MM/DD）') : ''}`
            }
          />
          {action === 'edit' && data && (
            <Box display="flex" gap={2}>
              <TextField
                name="createdAt"
                label="作成日時"
                disabled
                value={data?.createdAt?.toLocaleString('ja-JP') ?? '-'}
              />
              <TextField
                name="createdBy"
                label="作成者"
                fullWidth
                sx={{ flex: 1 }}
                disabled
                value={data.createdBy ?? '-'}
              />
              <TextField
                name="modifiedAt"
                label="変更日時"
                fullWidth
                sx={{ flex: 1 }}
                disabled
                value={data.modifiedAt?.toLocaleString('ja-JP') ?? '-'}
              />
              <TextField
                name="modifiedBy"
                label="変更者"
                fullWidth
                sx={{ flex: 1 }}
                disabled
                value={data.modifiedBy ?? '-'}
              />
            </Box>
          )}
        </Stack>
      </FormProvider>
    </Component>
  );
};
